import React, { useEffect } from "react";
import { Flex } from "components-lib";
import { SearchBar } from "components/navbar/searchBar/SearchBar";
import AddPatientForm from "../../AddPatientForm";
import NavigationTabs from "./NavigationTabs";
import GlobalFiltersBar from "components/GlobalFilters/GlobalFiltersBar";
import { useFocusMode } from "contexts/FocusModeContext";
/**
 * HeaderControls Component
 * Contains the main header controls including navigation tabs, search bar, and add patient form
 * Manages the top-level navigation and patient search functionality
 */
const HeaderControls = ({
  // Navigation state
  consultable,
  appoinmentable,
  showPatientList,
  showReports,
  showBills,

  // Navigation handlers
  onConsultationsClick,
  onAppointmentsClick,
  onPatientListClick,
  onReportsClick,
  onBillsClick,

  // Search functionality
  patientSearchQuery,
  setPatientSearchQuery,

  // Patient management
  clinicId,
  isDoctor,
  currentDoctorId,
  onPatientAdded,
  //role in clinic
  ric,
  appointmentStats,
  consultationsStats,
  // billing stats and active tab forwarded from parent
  billStats,
  activeTab,
  // Focus mode
  isConsultationActive = false,
}) => {
  const { setNavigationTabsContent } = useFocusMode();

  //Update navigation tabs content in focus mode context
  useEffect(() => {
    if (isConsultationActive) {
      // Set navigation tabs to be rendered in the navbar
      setNavigationTabsContent(
        <NavigationTabs
          ric={ric}
          consultable={consultable}
          appoinmentable={appoinmentable}
          showPatientList={showPatientList}
          showReports={showReports}
          showBills={showBills}
          onConsultationsClick={onConsultationsClick}
          onAppointmentsClick={onAppointmentsClick}
          onPatientListClick={onPatientListClick}
          onReportsClick={onReportsClick}
          onBillsClick={onBillsClick}
          compact={true}
        />
      );
    } else {
      // Clear navigation tabs from navbar
      setNavigationTabsContent(null);
    }
  }, [
    isConsultationActive,
    ric,
    consultable,
    appoinmentable,
    showPatientList,
    showReports,
    showBills,
    onConsultationsClick,
    onAppointmentsClick,
    onPatientListClick,
    onReportsClick,
    onBillsClick,
    setNavigationTabsContent,
  ]);

  return (
    <Flex
      mb="10px"
      p={isConsultationActive ? 2 : 4}
      bg={isConsultationActive ? "transparent" : "white"}
      borderRadius={isConsultationActive ? "none" : "lg"}
      boxShadow={isConsultationActive ? "none" : "sm"}
      flexDirection="column"
      gap={4}
      width="100%"
    >
      {/* When in focus mode, tabs are rendered in navbar via context */}
      {isConsultationActive ? null : (
        <>
          {/* Normal mode: tabs on one row, search/add on the same row */}
          <Flex width="100%" justify="space-between" align="center" gap={4}>
            {/* Navigation Tabs */}
            <NavigationTabs
              ric={ric}
              consultable={consultable}
              appoinmentable={appoinmentable}
              showPatientList={showPatientList}
              showReports={showReports}
              showBills={showBills}
              onConsultationsClick={onConsultationsClick}
              onAppointmentsClick={onAppointmentsClick}
              onPatientListClick={onPatientListClick}
              onReportsClick={onReportsClick}
              onBillsClick={onBillsClick}
              compact={false}
            />

            {/* Search and Add Patient Controls */}
            <Flex gap={4} align="center" flex={1} justify="flex-end" ml={8}>
              <SearchBar
                placeholder="Search Patient..."
                w={{ base: "100%", md: "200px" }}
                borderRadius="30px"
                value={patientSearchQuery}
                onChange={(e) => setPatientSearchQuery(e.target.value)}
              />
              <AddPatientForm
                clinicId={clinicId}
                doctorId={isDoctor ? currentDoctorId : null}
                onPatientAdded={onPatientAdded}
              />
            </Flex>
          </Flex>

          {/* Global filters - only when NOT in focus mode */}
          <GlobalFiltersBar
            appoinmentable={appoinmentable}
            appointmentStats={appointmentStats}
            consultationsStats={consultationsStats}
            consultable={consultable}
            // forward billing stats and whether Bills tab is active
            billsStats={billStats}
            showBills={activeTab === "Bills" || showBills}
          />
        </>
      )}
    </Flex>
  );
};

export default HeaderControls;
